<template>
    <el-dialog
        v-model="visible"
        :title="isEdit ? '编辑红外探测设备' : '添加红外探测设备'"
        width="600px"
        append-to-body
        :close-on-click-modal="false"
        @close="handleClose"
    >
        <el-form ref="formRef" :model="form" :rules="rules" label-width="130px" label-position="right" style="padding: 10px 0">
            <el-form-item label="设备名称" prop="name">
                <el-input v-model="form.name" placeholder="请输入设备名称" clearable />
            </el-form-item>
            <el-form-item label="IMEI" prop="IMEI">
                <el-input v-model="form.IMEI" placeholder="请输入IMEI" clearable :disabled="isEdit" />
            </el-form-item>

            <el-form-item label="安装位置" prop="room">
                <el-cascader
                    v-model="form.room"
                    :options="cascaderOptions"
                    :props="cascaderProps"
                    placeholder="请选择安装位置"
                    style="width: 100%"
                    filterable
                    clearable
                    @change="handleRoomChange"
                />
            </el-form-item>

            <el-form-item label="温控器组" prop="thermostat_group">
                <el-select v-model="form.thermostat_group" placeholder="请选择温控器组" style="width: 100%" filterable clearable>
                    <el-option v-for="item in groupList" :key="item.id" :label="item.name" :value="item.id" />
                </el-select>
            </el-form-item>
            <el-form-item label="设备无人时" prop="model_nobody">
                <el-radio-group v-model="form.model_nobody" style="display: flex; align-items: center">
                    <el-radio label="1">关</el-radio>
                    <el-radio label="2">调高温度</el-radio>
                </el-radio-group>
            </el-form-item>
            <el-form-item v-if="form.model_nobody === '2'" label="调高温度（℃）" prop="set_temp">
                <el-input-number
                    v-model="form.set_temp"
                    style="width: 200px"
                    placeholder="请输入温度"
                    clearable
                    :max="30"
                    :min="20"
                    :step="1"
                />
            </el-form-item>

            <el-form-item style="text-align: center; margin-top: 20px">
                <el-button type="primary" @click="submit" :loading="submitLoading">提交</el-button>
                <el-button @click="handleClose">取消</el-button>
            </el-form-item>
        </el-form>
    </el-dialog>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { addIrDetection, editIrDetection } from '@/api/thermostat/ir_detection.js'
import { getGroupList } from '@/api/thermostat/devicePoint.js'
import { getOptionTree } from '@/api/room.js'

const emit = defineEmits(['success'])

const visible = ref(false)
const formRef = ref(null)
const submitLoading = ref(false)
const isEdit = ref(false)
const roomTreeData = ref([])

// 存储节点映射，用于快速查找节点信息
const roomNodeMap = ref(new Map())

const groupList = ref([])

const form = reactive({
    id: '',
    name: '',
    building: '',
    IMEI: '',
    room: '',
    thermostat_group: '',
    model_nobody: '1', // 默认关
    set_temp: 24
})

// 级联选择器配置
const cascaderProps = {
    value: 'id',
    label: 'name',
    children: 'children',
    emitPath: false, // 只返回最后一级的值
    checkStrictly: false // 必须选择到叶子节点
}

// 过滤树数据：只保留含有 room_type 的节点及其父节点路径
function filterRoomTypeNodes(nodes) {
    return nodes
        .map((node) => {
            const newNode = { ...node }

            // 如果有子节点，递归过滤
            if (node.children && node.children.length > 0) {
                newNode.children = filterRoomTypeNodes(node.children)
            }

            // 当前节点有 room_type，或过滤后仍有子节点，则保留
            const hasRoomType = node.room_type !== undefined && node.room_type !== null && node.room_type !== ''
            const hasValidChildren = newNode.children && newNode.children.length > 0

            if (hasRoomType || hasValidChildren) {
                // 如果没有有效子节点且自身没有 room_type，则移除 children
                if (!hasValidChildren && !hasRoomType) {
                    delete newNode.children
                }
                return newNode
            }
            return null
        })
        .filter(Boolean)
}

// 级联选择器选项（过滤后）
const cascaderOptions = computed(() => {
    return filterRoomTypeNodes(roomTreeData.value)
})

// 表单验证规则
const rules = {
    IMEI: [
        { required: true, message: '请输入IMEI', trigger: 'blur' },
        { pattern: /^[0-9a-zA-Z]+$/, message: 'IMEI只能包含数字和字母', trigger: 'blur' }
    ],
    room: [{ required: true, message: '请选择安装位置', trigger: 'change' }],
    thermostat_group: [{ required: true, message: '请选择温控器组', trigger: 'change' }]
}

// 构建节点映射表
function buildNodeMap(list, map) {
    list.forEach((item) => {
        map.set(item.id, item)
        if (item.children && item.children.length > 0) {
            buildNodeMap(item.children, map)
        }
    })
}

// 安装位置改变时，根据节点类型设置 room 和 building
function handleRoomChange(nodeId) {
    if (!nodeId) {
        form.room = ''
        form.building = ''
        return
    }

    const node = roomNodeMap.value.get(nodeId)
    if (!node) return

    // 节点含有 room_type，room=节点id，building=节点的pid
    form.room = nodeId
    form.building = node.pid || node.parent_id || ''
}

// 加载温控器组列表
async function loadGroupList() {
    try {
        const res = await getGroupList({ page: 1, page_size: 999 })
        groupList.value = res?.lists || []
    } catch (err) {
        console.log('获取温控器组列表失败', err)
        groupList.value = []
    }
}

// 加载房间树
async function loadRoomTree() {
    try {
        const res = await getOptionTree()
        const treeData = res?.[0]?.children || res || []
        roomTreeData.value = treeData

        // 构建节点映射
        roomNodeMap.value = new Map()
        buildNodeMap(treeData, roomNodeMap.value)
    } catch (err) {
        console.log('获取房间树失败', err)
        roomTreeData.value = []
        roomNodeMap.value = new Map()
    }
}

// 打开弹窗
function open(data = {}) {
    visible.value = true
    isEdit.value = !!data.id

    // 重置表单
    reset()

    // 加载温控器组
    loadGroupList()

    if (isEdit.value) {
        // 编辑模式填充数据
        Object.assign(form, {
            id: data.id,
            building: data.building,
            IMEI: data.IMEI || '',
            room: data.room || '',
            thermostat_group: data.thermostat_group || '',
            name: data.name || '',
            model_nobody: data.model_nobody?.toString() || '',
            set_temp: data.set_temp || ''
        })
    } else {
        // 新增模式
        form.building = data.building || ''
    }
}

// 关闭弹窗
function handleClose() {
    visible.value = false
    reset()
}

// 重置
function reset() {
    if (formRef.value) {
        formRef.value.resetFields()
        formRef.value.clearValidate()
    }

    form.id = ''
    form.building = ''
    form.IMEI = ''
    form.room = ''
    form.thermostat_group = ''
    form.name = ''
    form.model_nobody = ''
    form.set_temp = ''
}

// 提交
async function submit() {
    if (!formRef.value) return

    const valid = await formRef.value.validate().catch(() => false)
    if (!valid) return

    submitLoading.value = true

    const submitData = {
        IMEI: form.IMEI,
        room: form.room,
        building: form.building,
        thermostat_group: form.thermostat_group,
        name: form.name || '',
        model_nobody: form.model_nobody || '',
        set_temp: form.set_temp || ''
    }
    if (isEdit.value) {
        submitData.id = form.id
    }

    try {
        if (isEdit.value) {
            await editIrDetection(submitData)
            ElMessage.success('编辑成功')
        } else {
            await addIrDetection(submitData)
            ElMessage.success('添加成功')
        }
        visible.value = false
        emit('success')
        reset()
    } catch (err) {
        console.log('提交失败', err)
        ElMessage.error(err?.message || '操作失败')
    } finally {
        submitLoading.value = false
    }
}

onMounted(() => {
    loadRoomTree()
})

defineExpose({ open })
</script>

<style scoped></style>
